import { PlusCircle, XIcon } from "lucide-react";
import {
  ComponentProps,
  Fragment,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import cn from "@/helpers/classNames";
import { Listbox, Transition } from "@headlessui/react";

export const FilterButton = forwardRef(function FilterButtonWithRef<
  TValue extends { label: string; icon?: ReactNode },
>(
  props: {
    label?: string;
    value: TValue | undefined | null;
    placeholder?: string;
    onClear: () => void;
  } & Omit<ComponentProps<"button">, "value">,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { value, placeholder, onClear, ...buttonProps } = props;
  return (
    <button
      ref={ref}
      {...buttonProps}
      className={cn(
        "group relative flex h-8 w-full cursor-default items-center gap-1.5 overflow-hidden rounded-sm border border-dashed px-2.5 text-left text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700",
        {
          "border-gray-300 bg-gray-100 hover:bg-gray-200 dark:border-gray-600 dark:bg-black/20 hover:dark:bg-black/40":
            value != null,
        },
        props.className,
      )}
    >
      {props.value == null && <PlusCircle className="h-4 w-4" />}
      {props.label}
      {props.value != null && (
        <>
          <div
            className={cn("mx-1 h-full w-px shrink-0 bg-border", {
              "bg-gray-300 dark:bg-gray-600": value != null,
            })}
          />
          {value?.icon &&
            cloneElement(value.icon as ReactElement, {
              className: "h-4 w-4 flex-none",
            })}
          <span className="max-w-32 truncate text-xs font-normal">
            {value?.label}
          </span>
        </>
      )}
      {props.value != null && (
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="absolute right-1 top-1/2 z-10 hidden aspect-square h-6 -translate-y-1/2 items-center justify-center rounded bg-background shadow-lg hover:border-red-300 hover:bg-red-200 hover:text-danger group-focus-within:flex group-hover:flex"
        >
          <XIcon className="h-4 w-4" />
        </div>
      )}
    </button>
  );
});

export function SingleSelectFilter(props: {
  name?: string;
  options: {
    label: string;
    value: string | boolean | null;
    count: number;
    icon?: ReactNode;
  }[];
  value: { label: string; value: string | boolean | null } | null | undefined;
  placeholder?: string;
  onChange: (value: { label: string; value: string | boolean | null }) => void;
  onClear: () => void;
}) {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLUListElement | null>(
    null,
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "bottom-start",
    modifiers: [
      { name: "preventOverflow", enabled: true },
      { name: "offset", options: { offset: [0, 4] } },
    ],
  });

  return (
    <Listbox
      as="div"
      className="relative"
      value={props.value}
      onChange={props.onChange}
    >
      <Listbox.Button
        ref={setReferenceElement}
        as={FilterButton}
        value={props.value}
        onClear={props.onClear}
        placeholder={props.placeholder}
        label={props.name}
      />
      {createPortal(
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options
            ref={(elm) => setPopperElement(elm as HTMLUListElement | null)}
            style={styles.popper}
            {...attributes.popper}
            className="absolute z-10 max-h-80 w-fit max-w-sm overflow-auto rounded-sm bg-background py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-900"
          >
            {props.options.map((option) => (
              <Listbox.Option
                key={option.label}
                value={option}
                className={({ active, selected }) =>
                  cn(
                    `relative flex cursor-default select-none items-center px-4 py-2`,
                    {
                      "bg-blue-500 text-white": active || selected,
                    },
                  )
                }
              >
                {({ active, selected }) => (
                  <>
                    {option.icon &&
                      cloneElement(option.icon as ReactElement, {
                        className: "mr-2 h-5 w-5 flex-none",
                      })}
                    <div className="flex-1 truncate">
                      <span>{option.label}</span>
                    </div>
                    <span
                      className={cn(
                        "ml-2 flex-none text-xs text-muted-foreground",
                        {
                          "text-gray-200": active || selected,
                        },
                      )}
                    >
                      {option.count}
                    </span>
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>,
        document.body,
      )}
    </Listbox>
  );
}
